import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from '../hooks/useTranslation';

const Header = ({ onSignIn, user, isSigningIn }: { onSignIn?: () => void, user?: any, isSigningIn?: boolean }) => {
  const { t, isBrazil } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isHome = location.pathname === '/';

  if (isDashboard) return null;

  const navLinks = [
    { name: t.features, href: '/features' },
    { name: t.pricing, href: '/pricing' },
    { name: t.howItWorks, href: '/how-it-works' },
    { name: t.portfolio, href: '/showcase' },
    { name: t.blog, href: '/blog' },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isHome ? 'bg-brand-dark/80 backdrop-blur-md border-b border-white/[0.05]' : 'bg-white/80 backdrop-blur-md border-b border-gray-100'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 flex items-center justify-center group-hover:rotate-12 transition-transform duration-300 overflow-hidden">
              <img src="/PlataPost-P.png" alt="P" className="w-10 h-10 object-contain" />
            </div>
            <span className={`text-2xl font-black tracking-tighter ${isHome ? 'text-white' : 'bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600'}`}>
              PlataPost
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm font-bold transition-colors ${isHome ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-indigo-600'}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-6">
            {user ? (
              <Link
                to="/dashboard"
                className="px-6 py-2.5 bg-indigo-600 text-white rounded-full text-sm font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/20"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <button 
                  onClick={onSignIn}
                  disabled={isSigningIn}
                  className={`text-sm font-bold transition-colors disabled:opacity-50 flex items-center gap-2 ${isHome ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-indigo-600'}`}
                >
                  {isSigningIn ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t.login}...</span>
                    </>
                  ) : t.login}
                </button>
                <button
                  onClick={onSignIn}
                  disabled={isSigningIn}
                  className="px-6 py-2.5 bg-indigo-600 text-white rounded-full text-sm font-bold hover:bg-indigo-700 transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] disabled:opacity-50 flex items-center gap-2"
                >
                  {isSigningIn ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {isSigningIn ? `${t.startFree}...` : t.startFree}
                </button>
              </>
            )}
          </div>

          {/* Mobile Toggle */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 focus:outline-none ${isHome ? 'text-white' : 'text-gray-600'}`}
            >
              {isOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`lg:hidden border-b overflow-hidden shadow-xl ${isHome ? 'bg-brand-dark border-white/[0.05]' : 'bg-white border-gray-100'}`}
          >
            <div className="px-6 pt-2 pb-8 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-4 text-lg font-bold rounded-2xl transition-all ${isHome ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-50'}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-6 flex flex-col gap-4">
                <button
                  onClick={() => { onSignIn?.(); setIsOpen(false); }}
                  disabled={isSigningIn}
                  className={`w-full text-center py-4 text-lg font-bold rounded-2xl border transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${isHome ? 'text-white border-white/10 hover:bg-white/5' : 'text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                >
                  {isSigningIn && <Loader2 className="w-6 h-6 animate-spin" />}
                  {isSigningIn ? `${t.login}...` : t.login}
                </button>
                <button
                  onClick={() => { onSignIn?.(); setIsOpen(false); }}
                  disabled={isSigningIn}
                  className="w-full text-center py-4 bg-indigo-600 text-white rounded-2xl font-black text-lg shadow-lg shadow-indigo-600/20 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSigningIn && <Loader2 className="w-6 h-6 animate-spin" />}
                  {isSigningIn ? `${t.startFree}...` : t.startFree}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
